<template>
  <b-card no-body class="additional-information-section">
    <FormulateForm ref="formAdditionalRef" v-model="formValues">
      <template v-if="hasAccess(['fleet-task-instant-sales-order:read'])">
        <h5 class="m-0 mb-2">
          {{ $t('fleetTaskInstant.title.salesOrderInformation') }}
        </h5>
        <b-row>
          <b-col v-for="field in salesOrderInformationFields" :key="field.key" cols="3">
            <FormulateInput
              v-bind="field"
              validation-behavior="live"
              error-behavior="live"
              :disabled="isLoadingGlobal"
              :fullObject.sync="fullObjects[field.name]"
              @input="
                handleInput({ type: field._type ?? field.type, key: field.name, value: $event })
              "
            />
          </b-col>
        </b-row>
        <hr />
      </template>

      <div
        v-if="
          hasAccess([
            'fleet-task-instant-driver-attachment:create',
            'fleet-task-instant-driver-attachment:read',
            'fleet-task-instant-driver-attachment:update',
            'fleet-task-instant-driver-attachment:delete',
          ])
        "
        class="additional-information-section__attachments"
      >
        <h5 class="__title">
          {{ $t('fleetTaskInstant.title.additionalDocument') }}
        </h5>

        <p class="__driver-document-title">
          {{ $t('fleetTaskInstant.field.driverDocument') }}
        </p>

        <div class="__driver-attachments">
          <div v-for="(attachment, idx) in attachments" :key="attachment.id" class="attachment">
            <div
              class="attachment__wrapper"
              :class="
                attachment.type === 'FLEET_TASK_INSTANT_DRIVER_ATTACHMENT_PHOTO'
                  ? '__photo'
                  : '__file'
              "
              @click="activeAttachmentIdx = idx"
            >
              <img
                v-if="attachment.name.endsWith('pdf')"
                src="@/assets/images/download-format/pdf.png"
                alt
              />

              <img
                v-else-if="attachment.name.endsWith('xlsx')"
                src="@/assets/images/download-format/excel.png"
                alt
              />

              <img v-else :src="attachment.url" alt />

              <button
                class="__delete-btn"
                :disabled="isLoadingGlobal"
                @click.stop="handleDeleteAttachment(attachment.id)"
              >
                <i class="icon-outline_trash"></i>
              </button>
            </div>

            <p class="attachment__name">{{ attachment.name }}</p>
          </div>
        </div>

        <McButton
          icon="icon-outline_upload"
          :text="$t('fleetTaskInstant.action.uploadDocument')"
          :disabled="isLoadingGlobal || attachments.length >= 10"
          @click="$refs.fileInputRef.click()"
        />

        <input
          ref="fileInputRef"
          type="file"
          multiple
          hidden
          :accept="fileAccepted.join(', ')"
          @change="handleAddDocuments"
        />
        <hr />
      </div>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="m-0">
          {{ $t('fleetTaskInstant.title.additionalField') }}
        </h5>
        <McButton
          variant="secondary"
          class="ms-3"
          :text="$t('global.action.create')"
          icon="icon-outline_plus"
          :disabled="isLoadingGlobal"
          @click="handleShowCreateModal"
        />
      </div>
      <b-row v-if="isCustomFieldShown">
        <b-col v-for="field in customFields" :key="field.key" cols="3">
          <FormulateInput
            v-bind="field"
            validation-behavior="live"
            error-behavior="live"
            :disabled="field.type == 'date-picker' ? isLoadingGlobal : false"
            :error="formErrors[`custom_field.${field.name}`]"
            @input="
              handleInput({ type: field._type ?? field.type, key: field.name, value: $event })
            "
          />
        </b-col>
      </b-row>
    </FormulateForm>

    <ModalForm
      ref="modal-form"
      :formType="formType"
      :customSuccessMsg="$t('fleetTaskInstant.toast.customField.successCreate')"
      @reloadList="handleReload"
    />

    <PreviewImage
      v-if="activeAttachmentIdx !== null"
      :attachments="attachments"
      :activeAttachmentIdx="activeAttachmentIdx"
      :customHandlers="customHandlers"
      @update:activeAttachmentIdx="activeAttachmentIdx = $event"
    />
  </b-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { computed } from 'vue'
import Compressor from 'compressorjs'

import McButton from '@/components/McButton'
import PreviewImage from '../components/PreviewImage'

import ModalForm from '../../setting/custom-field/quick-assignment/components/ModalForm'
import { typeItems } from '../../setting/custom-field/quick-assignment/lib/constants/type-items'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import * as CustomField from '@/services/rest-api/lib/setting/quick-assignment/custom-field'
import uploadStorage from '@/services/rest-api/lib/fleet-task-instant/storage'

import salesOrderInformationFields from '../lib/fields/salesOrderInformationFields'

import { EventBus } from '../lib/event-bus.js'

import { cloneDeep, isEqual } from 'lodash'
import { errorV2Msg } from '../lib/constants/error-v2-mapping.js'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    McButton,
    ModalForm,
    PreviewImage,
  },
  mixins: [Helper, salesOrderInformationFields],
  provide() {
    return {
      typeItems: computed(() => this.typeItems),
    }
  },
  data() {
    return {
      typeItems,
      isInitial: true,

      defaultCustomFields: [],

      initialFormValues: {},
      formValues: {
        sales_order_id: null,
      },
      formErrors: {},

      fullObjects: {
        sales_order_id: {},
      },
      oldFullObjects: {},

      formType: null,

      activeAttachmentIdx: null,
      acceptFile: '.png, .jpg, .jpeg, .pdf',
      maxImageFiles: 10,
      maxFileSize: 2 * 1024 * 1024, //2MB
      customHandlers: {
        handleDownloadImg: async (imgUrl, fileName, file) => {
          const res = await fetch(imgUrl, {
            headers: new Headers({
              'Cache-Control': 'no-cache',
            }),
          })
          if (!res) return

          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          Object.assign(document.createElement('a'), { href: url, download: file.name }).click()
          URL.revokeObjectURL(url)
        },

        handlePathUrl: (file) => {
          return file.url
        },
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    ...mapState('fleetTaskInstant', ['isLoadingGlobal', 'showResponse']),
    id() {
      return this.$route.params.id
    },
    isCustomFieldShown() {
      return this.customFields.length > 0
    },
    customFields() {
      return this.transformCustomFieldResponse(this.defaultCustomFields)
    },
    fileAccepted() {
      return ['.png', '.jpg', '.jpeg', '.pdf', '.xlsx']
    },
    attachments() {
      const attachments = []

      this.showResponse?.attachment?.photos?.forEach((item) => {
        attachments.push({
          id: item.id,
          name: item.file_name,
          url: item.url,
          type: item.type,
        })
      })

      this.showResponse?.attachment?.documents?.forEach((item) => {
        attachments.push({
          id: item.id,
          name: item.file_name,
          url: item.url,
          type: item.type,
        })
      })

      return attachments
    },
    attachmentIds() {
      return this.attachments?.map?.((item) => item.id)
    },
  },
  created() {
    EventBus.$on('update-form-values', this.setFormValues)
    this.setFormValues()
  },
  async mounted() {
    await this.getDefaultCustomFields()
  },
  destroyed() {
    EventBus.$off('update-form-values', this.setFormValues)
  },
  methods: {
    setFormValues(opts = {}) {
      if (Object.keys(this.showResponse).length === 0) return

      if (this.showResponse.custom_field !== null) {
        Object.keys(this.showResponse.custom_field).forEach((key) => {
          if (opts.updatedKeys?.includes(key)) return

          this.$set(this.formValues, key, this.showResponse.custom_field[key])
          this.$set(this.initialFormValues, key, this.showResponse.custom_field[key])
        })
      }

      if (this.showResponse?.sales_order) {
        const value = this.showResponse?.sales_order
        this.$set(this.formValues, 'sales_order_id', value.id)
        this.$set(this.initialFormValues, 'sales_order_id', value.id)
        this.$set(this.fullObjects, 'sales_order_id', value?.id === null ? null : value)
      }

      this.$nextTick(() => {
        this.oldFullObjects = cloneDeep(this.fullObjects)
        this.isInitial = false
      })
    },

    async getDefaultCustomFields() {
      const res = await CustomField.get({
        limit: Number.MAX_SAFE_INTEGER,
        page: 1,
        filters: ['entity eq FLEET_TASK_INSTANT'],
      })

      if (res.errors) return

      this.defaultCustomFields = res.data.paginated_result
    },

    handleShowCreateModal() {
      this.formType = 'create'
      this.$refs['modal-form'].showModal()
    },

    async handleReload() {
      this.isInitial = true

      await this.getDefaultCustomFields()
      EventBus.$emit('refetch-fleet-task-instant')
    },

    transformCustomFieldResponse(fields = []) {
      const fieldTypeMapping = { TEXT: 'text', NUMERIC: 'number', TIMESTAMPTZ: 'date-picker' }
      const filteredFields = cloneDeep(fields).filter((field) => field.is_active)

      const transformed = cloneDeep(filteredFields).map((field) => {
        const validation = []

        if (field.type === 'NUMERIC') {
          field._type = fieldTypeMapping.NUMERIC
          field.type = fieldTypeMapping.TEXT
          validation.push('number')
        } else if (field.type === 'TIMESTAMPTZ') {
          field.type = fieldTypeMapping.TIMESTAMPTZ
          field.datepickerType = 'datetime'
          field.valueType = 'YYYY-MM-DDTHH:mm:ssZ'
          field.format = 'DD MMM YYYY HH:mm'
          field.placeholder = this.$t('fleetTaskInstant.placeholder.datetime')
          ;(field.confirm = true), (field.confirmText = this.$t('global.action.apply'))
        } else if (field.type === 'TEXT') {
          field.type = fieldTypeMapping.TEXT
          validation.push('max:255,length')
        }

        if (validation.length > 0) {
          field.validation = validation.join('|')
        }

        field.label = field.name
        field.name = field.key

        return field
      })

      return transformed
    },

    handleInput: function ({ type, key, value }) {
      if (this.isInitial) return
      EventBus.$emit('user-typing-started')

      if (!['text', 'number'].includes(type) && this.isLoadingGlobal) return

      let opts = {}
      if (['text', 'number'].includes(type)) {
        opts.updatedKeys = [key]
      }

      clearTimeout(this.debounceTimer)
      const self = this
      this.debounceTimer = setTimeout(async () => {
        if (self.$refs.formAdditionalRef.hasErrors) return

        if (type == 'number') {
          value = Number(value) || null
        }

        if (key === 'sales_order_id' && (value === null || value === undefined || value === '')) {
          value = null
        }

        self.$set(self.formValues, key, value)

        self.$delete(self.formErrors, `custom_field.${key}`)

        self.updateDetails(opts)
      }, 500)
    },

    async updateDetails(opts = {}) {
      this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)

      const data = this.filterUnchangedData(this.formValues, this.initialFormValues)

      const { sales_order_id, ...customFields } = data[0]

      const updatePayload = {}
      if (sales_order_id !== undefined) {
        updatePayload.sales_order_id = sales_order_id
      }

      if (Object.keys(customFields).length > 0) {
        updatePayload.custom_field = customFields
      }

      if (Object.keys(updatePayload).length === 0) {
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
        return
      }

      const res = await FleetTaskInstant.update(this.id, updatePayload)

      if (res.errors_v2) {
        this.mapErrorsV2(res.errors_v2)

        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
        return
      }

      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)

      EventBus.$emit('refetch-fleet-task-instant', 'periodic', null, {
        type: 'custom-field',
        updatedKeys: opts.updatedKeys,
      })
    },

    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) return

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        // if it is array
        if (Array.isArray(oldValue[key]) || Array.isArray(newValue[key])) {
          // if oldValue is undefined but the newValue is empty array
          if (!oldValue[key] && newValue[key].length === 0) return

          // matching oldValue and newValue array
          const sortedNewValue = newValue[key]?.length ? newValue[key].sort() : newValue[key]
          const sortedOldValue = oldValue[key] ? oldValue[key].sort() : oldValue[key]

          const isSameArray = isEqual(sortedNewValue, sortedOldValue)

          if (isSameArray) return
        }

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },

    mapErrorsV2(errorsv2) {
      this.formErrors = {}
      if (errorsv2.issues && errorsv2.issues.length > 0) {
        errorsv2.issues.forEach((issue) => {
          let key = issue.path.join('.')

          let errorMsg = errorV2Msg[issue.code]

          if (typeof errorMsg === 'function') {
            errorMsg = errorMsg()
          }

          this.$set(this.formErrors, key, errorMsg || this.$t('global.inputFormError'))
        })
      }
    },

    async handleAddDocuments(e) {
      try {
        const files = e.target.files

        // Validate file counts
        if (files.length + this.attachments.length > this.maxImageFiles) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTaskInstant.toast.driverAttachment.exceedMaxDoc'),
                },
              ],
            },
          })
          return
        }

        const attachments = []
        for (let i = 0; i < files.length; i++) {
          const file = files[i]

          // Validate file type
          const fileTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'image/png',
            'image/jpg',
            'image/jpeg',
            'application/pdf',
          ]
          if (!fileTypes.includes(file.type)) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTaskInstant.toast.driverAttachment.invalidExt'),
                  },
                ],
              },
            })
            return
          }

          // Validate file size
          if (file.size > this.maxFileSize) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTaskInstant.toast.driverAttachment.exceedMaxDocSize'),
                  },
                ],
              },
            })
            return
          }

          let processedFile = file

          if (['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
            processedFile = await new Promise((resolve, reject) => {
              new Compressor(file, {
                maxWidth: 1600,
                maxHeight: 1600,
                quality: 0.8,
                success: resolve,
                error: reject,
              })
            })
          }

          attachments.push(processedFile)
        }

        await this.handleUploadDocuments(attachments)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error selecting files:', error)
          this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedUpload'))
        }
      } finally {
        this.$refs.fileInputRef.value = null
      }
    },

    async handleUploadDocuments(attachments) {
      this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)

      const promises = []

      attachments.forEach((item) => {
        promises.push(uploadStorage(item))
      })

      const responses = await Promise.all(promises)

      const ids = [...this.attachmentIds]
      let isError = false

      responses.forEach((res) => {
        if (res.errors) {
          isError = true
          return
        }

        ids.push(res.data.id)
      })

      if (isError) {
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedUpload'))
        return
      }

      const resUpload = await FleetTaskInstant.update(this.id, {
        driver_attachment_file_ids: ids,
      })

      if (resUpload.errors) {
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedUpload'))
        return
      }

      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
      EventBus.$emit('refetch-fleet-task-instant', 'periodic', null, {})
      this.$mcToast.success(this.$t('fleetTaskInstant.toast.driverAttachment.successUpload'))
    },

    async handleDeleteAttachment(id) {
      this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)

      const ids = this.attachmentIds.filter((item) => item !== id)

      const resUpload = await FleetTaskInstant.update(this.id, {
        driver_attachment_file_ids: ids,
      })

      if (resUpload.errors) {
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedDelete'))
        return
      }

      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
      EventBus.$emit('refetch-fleet-task-instant', 'periodic', null, {})
      this.$mcToast.success(this.$t('fleetTaskInstant.toast.driverAttachment.successDelete'))
    },
  },
}
</script>

<style lang="scss" scoped>
.additional-information-section {
  width: 100%;
  height: 100%;
  padding: 20px;
  border: 1px solid $mep-primary-100;
  overflow-y: auto;

  &__attachments {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    .__driver-document-title {
      color: $mep-color-text-300;
      margin: 0;
    }

    .__driver-attachments {
      display: flex;
      gap: 8px;
    }

    .attachment {
      position: relative;
      max-width: 80px;

      &__wrapper {
        width: 80px;
        height: 80px;
        border-radius: 4px;
        overflow: hidden;
        cursor: pointer;

        img {
          object-fit: cover;
        }

        &.__photo {
          img {
            width: 100%;
            height: 100%;
          }
        }

        &.__file {
          border: 1px dashed $mep-color-text-300;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }

      &__name {
        margin-top: 8px;
        font-size: 0.8em;
        font-weight: 600;
        text-align: center;

        width: 80px;
        overflow: hidden;

        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;

        color: $primary;
      }

      .__delete-btn {
        position: absolute;
        top: 3px;
        right: 3px;
        height: 18px;
        width: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $red;
        border-radius: 3px;
        background-color: $white;

        i::before {
          font-size: 13px;
          color: $red;
        }
      }

      .__delete-btn:hover {
        background-color: $red;

        i::before {
          color: $white;
        }
      }
    }
  }
}
</style>
